import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInvoicePermissions } from '../../hooks/useInvoicePermissions';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptIcon
} from '@mui/icons-material';
import * as invoiceService from '../../services/invoiceService';
import * as printTemplateService from '../../services/printTemplateService';
import { IInvoice, IInvoicePage, IInvoiceItem } from '../../types/invoice';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import InvoicePaymentSection from './InvoicePaymentSection';
import ZatcaSubmissionStatus from './ZatcaSubmissionStatus';

const InvoiceViewPage: React.FC = () => {
  const { pageId, invoiceId } = useParams<{ pageId: string; invoiceId: string }>();
  const navigate = useNavigate();
  const { hasInvoicePagePermission } = useInvoicePermissions();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<IInvoice | null>(null);
  const [page, setPage] = useState<IInvoicePage | null>(null);
  const [printTemplatesDialog, setPrintTemplatesDialog] = useState(false);
  const [printTemplates, setPrintTemplates] = useState<{id: string, name: string}[]>([]);

  // التحقق من صلاحيات الوصول للصفحة وعرض الفاتورة - تمرير المعرف كاملاً
  const canViewInvoice = pageId ? hasInvoicePagePermission(pageId, 'view_page') : false;
  const canPrintInvoice = pageId ? hasInvoicePagePermission(pageId, 'print') : false;

  // Fetch invoice data function
  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (!invoiceId) {
        throw new Error('معرف الفاتورة غير موجود');
      }

      // جلب تفاصيل الفاتورة
      const invoiceData = await invoiceService.getInvoiceById(invoiceId);
      console.log('Invoice data:', invoiceData);
      console.log('Invoice tax value:', invoiceData.tax);
      // Check item tax details
      if (invoiceData.items && invoiceData.items.length > 0) {
        console.log('First item:', invoiceData.items[0]);
        console.log('First item price:', invoiceData.items[0].price);
        console.log('First item tax:', invoiceData.items[0].tax);
      }
      setInvoice(invoiceData);

      // جلب تفاصيل صفحة الفاتورة (للحصول على قوالب الطباعة)
      if (pageId) {
        const pageData = await invoiceService.getInvoicePageById(pageId);
        setPage(pageData);
      }
      
      setError(null);
    } catch (err: any) {
      console.error('خطأ في تحميل بيانات الفاتورة:', err);
      setError(err.message || 'حدث خطأ أثناء تحميل بيانات الفاتورة. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoice data on component mount
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, pageId]);

  // Handle print invoice
  const handlePrintInvoice = async () => {
    if (!invoice || !pageId) return;
    
    try {
      // Get the page data to check if it has print templates
      const pageData = page || await invoiceService.getInvoicePageById(pageId);
      
      if (pageData.printTemplates && pageData.printTemplates.length > 0) {
        // جلب بيانات قوالب الطباعة للحصول على أسمائها
        const templatesWithNames = await Promise.all(
          pageData.printTemplates.map(async (templateId: string) => {
            try {
              // محاولة جلب اسم القالب من الخادم
              const template = await printTemplateService.getPrintTemplateById(templateId);
              return { id: templateId, name: template.name || templateId };
            } catch (err) {
              // في حالة عدم القدرة على جلب القالب، استخدم المعرف كاسم
              return { id: templateId, name: templateId };
            }
          })
        );
        
        // تعيين قوالب الطباعة مع أسمائها
        setPrintTemplates(templatesWithNames);
        // فتح حوار اختيار القوالب
        setPrintTemplatesDialog(true);
      } else if (pageData.defaultPrintTemplate) {
        // إذا كان هناك قالب افتراضي فقط، استخدمه مباشرة
        window.open(`/api/invoices/${invoice._id}/print?templateId=${pageData.defaultPrintTemplate}`, '_blank');
      } else {
        // إذا لم تكن هناك قوالب متاحة، استخدم وظيفة الطباعة الافتراضية
        invoiceService.printInvoice(invoice._id!);
      }
    } catch (err) {
      console.error('خطأ في تحضير قوالب الطباعة:', err);
      // في حالة حدوث خطأ، استخدم وظيفة الطباعة الافتراضية
      invoiceService.printInvoice(invoice._id!);
    }
  };

  // Handle printing with selected template
  const handlePrintWithTemplate = (templateId: string) => {
    if (!invoice) return;
    
    // Open a new window with the print endpoint and the selected template
    window.open(`/api/invoices/${invoice._id}/print?templateId=${templateId}`, '_blank');
    
    // Close the dialog
    setPrintTemplatesDialog(false);
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount);
  };

  // Go back to invoice list
  const handleBackToList = () => {
    navigate(`/invoices/${pageId}`);
  };

  // Render loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  // Render content if invoice exists
  // التحقق من صلاحيات الوصول للصفحة فقط (مرحلة أولى)
  if (!canViewInvoice) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        ليس لديك صلاحية للوصول إلى هذه الصفحة
      </Alert>
    );
  }
  
  // لوجود المستخدم على الصفحة، سنعرض الفاتورة بتفاصيل محدودة إذا لم تكن لديه صلاحية مشاهدة الفاتورة

  if (!invoice) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        لم يتم العثور على الفاتورة
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display="flex" alignItems="center">
          <ReceiptIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" component="h1">
            مشاهدة الفاتورة - {invoice.invoiceNumber}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            sx={{ mr: 1 }}
          >
            العودة للقائمة
          </Button>
          {canPrintInvoice && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrintInvoice}
            >
              طباعة الفاتورة
            </Button>
          )}
        </Box>
      </Paper>

      {/* Invoice Details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                معلومات الفاتورة
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    رقم الفاتورة
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {invoice.invoiceNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    التاريخ والوقت
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {(() => {
                      // استخراج التاريخ والوقت مباشرة من النص بدون تحويل
                      const dateStr = invoice.date;
                      const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
                      if (match) {
                        const [, year, month, day, hours, minutes] = match;
                        return (
                          <>
                            {`${day}/${month}/${year}`}
                            {' '}
                            <Chip
                              size="small"
                              label={`${hours}:${minutes}`}
                              color="info"
                              sx={{ height: 22, fontSize: '0.75rem' }}
                            />
                          </>
                        );
                      }
                      // fallback: استخدام date-fns
                      return (
                        <>
                          {format(new Date(invoice.date), 'dd/MM/yyyy', { locale: ar })}
                          {' '}
                          <Chip
                            size="small"
                            label={format(new Date(invoice.date), 'HH:mm', { locale: ar })}
                            color="info"
                            sx={{ height: 22, fontSize: '0.75rem' }}
                          />
                        </>
                      );
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    الحالة
                  </Typography>
                  <Chip
                    label={invoice.status === 'completed' ? 'مكتملة' : 'مسودة'}
                    color={invoice.status === 'completed' ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    الفرع
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {invoice.branchName || 'غير محدد'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                معلومات {invoice.customerName ? 'العميل' : 'المورد'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" fontWeight="medium">
                {invoice.customerName || invoice.supplierName || 'غير محدد'}
              </Typography>
              {(invoice.customerName || invoice.supplierName) && (
                <Typography variant="body2" color="text.secondary">
                  الرقم: {invoice.customerId || invoice.supplierId || 'غير متوفر'}
                </Typography>
              )}
              {/* عرض رقم الهاتف */}
              {(invoice as any).customerPhone && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  الهاتف: {(invoice as any).customerPhone}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box
                display="flex"
                flexWrap="wrap"
                gap={1}
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">
                  عناصر الفاتورة
                </Typography>
                <Box sx={{
                  bgcolor: '#e3f2fd',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  border: '1px solid #bbdefb'
                }}>
                  <Typography variant="body2" color="primary.main" fontWeight="bold">
                    إجمالي الكميات: {invoice.items.reduce((total, item) => total + (Number(item.quantity) || 0), 0)} قطعة
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>المنتج</TableCell>
                    <TableCell>التفاصيل</TableCell>
                    <TableCell align="center">الكمية</TableCell>
                    {page?.enableTax && invoice.tax > 0 && (
                      <>
                        <TableCell align="center">السعر (شامل الضريبة)</TableCell>
                        <TableCell align="center">السعر (بدون ضريبة)</TableCell>
                        <TableCell align="center">نسبة الضريبة</TableCell>
                        <TableCell align="center">قيمة الضريبة</TableCell>
                      </>
                    )}
                    {(!page?.enableTax || invoice.tax <= 0) && (
                      <TableCell align="center">السعر</TableCell>
                    )}
                    <TableCell align="center">الخصم</TableCell>
                    <TableCell align="center">الإجمالي</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.items.map((item: IInvoiceItem & {
                    variantName?: string;
                    warehouseName?: string;
                    taxPercent?: number;
                    discountPercent?: number;
                  }, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.productName}
                          </Typography>
                          {/* عرض الباركود - استخراج القيمة النصية فقط */}
                          {(() => {
                            const barcode = item.barcode;
                            // إذا كان نصاً بسيطاً
                            if (barcode && typeof barcode === 'string' &&
                                barcode.length < 30 &&
                                !/[{}\[\]<=>$]/.test(barcode)) {
                              return (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  باركود: {barcode}
                                </Typography>
                              );
                            }
                            // إذا كان كائناً، لا نعرض شيئاً
                            return null;
                          })()}
                        </TableCell>
                        <TableCell>
                          {/* عرض اسم المتغير - فقط إذا كان نصاً بسيطاً */}
                          {(() => {
                            const variantName = item.variantName;
                            if (variantName && typeof variantName === 'string' &&
                                !/[${}[\]<=>]/.test(variantName)) {
                              return (
                                <Typography variant="caption" display="block">
                                  {variantName}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* عرض الخصائص من variantAttributes فقط */}
                          {(() => {
                            const itemAny = item as any;
                            // التحقق من وجود variantAttributes
                            if (!itemAny.variantAttributes || typeof itemAny.variantAttributes !== 'object') {
                              return null;
                            }
                            
                            const attrs: string[] = [];
                            try {
                              // استخراج الخصائص النظيفة فقط
                              for (const [key, value] of Object.entries(itemAny.variantAttributes)) {
                                // تجاهل الخصائص الداخلية والتقنية
                                if (key.startsWith('$') || key.startsWith('_') ||
                                    key.includes('parent') || key.includes('path') ||
                                    key.includes('schema')) {
                                  continue;
                                }
                                // عرض فقط القيم النصية البسيطة
                                if (value && typeof value === 'string' &&
                                    value.length < 50 && value.length > 0 &&
                                    !/[${}[\]<=>]/.test(value)) {
                                  attrs.push(`${key}: ${value}`);
                                }
                              }
                              
                              if (attrs.length > 0) {
                                return (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {attrs.join(" | ")}
                                  </Typography>
                                );
                              }
                            } catch (e) {
                              console.error('Error processing attributes:', e);
                            }
                            return null;
                          })()}
                          
                          {/* عرض المستودع - فقط إذا كان نصاً بسيطاً */}
                          {(() => {
                            const warehouseName = item.warehouseName;
                            if (warehouseName && typeof warehouseName === 'string' &&
                                !/[${}[\]<=>]/.test(warehouseName)) {
                              return (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  المستودع: {warehouseName}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        {page?.enableTax && invoice.tax > 0 && (
                          <>
                            <TableCell align="center">{formatCurrency(item.price)}</TableCell>
                            <TableCell align="center">
                              {(() => {
                                // حساب السعر بدون ضريبة
                                const itemAny = item as any;
                                const preTaxPrice = itemAny.preTaxPrice || (item.price / (1 + ((item.tax || 0) / 100)));
                                return formatCurrency(preTaxPrice);
                              })()}
                            </TableCell>
                            <TableCell align="center">{item.tax || 0}%</TableCell>
                            <TableCell align="center">
                              {(() => {
                                // حساب قيمة الضريبة
                                const itemAny = item as any;
                                const taxAmount = itemAny.taxAmount || ((item.price - (item.price / (1 + ((item.tax || 0) / 100)))) * item.quantity);
                                return formatCurrency(taxAmount);
                              })()}
                            </TableCell>
                          </>
                        )}
                        {(!page?.enableTax || invoice.tax <= 0) && (
                          <TableCell align="center">{formatCurrency(item.price)}</TableCell>
                        )}
                        <TableCell align="center">
                          {formatCurrency(item.discount || 0)}
                          {(() => {
                            const itemAny = item as any;
                            const discountPercent = itemAny.discountPercent;
                            if (discountPercent && discountPercent > 0) {
                              return (
                                <Typography variant="caption" display="block">
                                  ({discountPercent}%)
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.total)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TableContainer>

              {/* عرض المنتجات كبطاقات على الجوال */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {invoice.items.map((item: IInvoiceItem & {
                  variantName?: string;
                  warehouseName?: string;
                  taxPercent?: number;
                  discountPercent?: number;
                }, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      {/* رأس البطاقة: رقم المنتج والاسم والكمية */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                            {index + 1}. {item.productName}
                          </Typography>
                          {/* الباركود */}
                          {(() => {
                            const barcode = item.barcode;
                            if (barcode && typeof barcode === 'string' &&
                                barcode.length < 30 &&
                                !/[{}\[\]<=>$]/.test(barcode)) {
                              return (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  باركود: {barcode}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                          {/* اسم المتغير */}
                          {(() => {
                            const variantName = item.variantName;
                            if (variantName && typeof variantName === 'string' &&
                                !/[${}[\]<=>]/.test(variantName)) {
                              return (
                                <Typography variant="caption" display="block">
                                  {variantName}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                          {/* الخصائص */}
                          {(() => {
                            const itemAny = item as any;
                            if (!itemAny.variantAttributes || typeof itemAny.variantAttributes !== 'object') {
                              return null;
                            }
                            const attrs: string[] = [];
                            try {
                              for (const [key, value] of Object.entries(itemAny.variantAttributes)) {
                                if (key.startsWith('$') || key.startsWith('_') ||
                                    key.includes('parent') || key.includes('path') ||
                                    key.includes('schema')) {
                                  continue;
                                }
                                if (value && typeof value === 'string' &&
                                    value.length < 50 && value.length > 0 &&
                                    !/[${}[\]<=>]/.test(value)) {
                                  attrs.push(`${key}: ${value}`);
                                }
                              }
                              if (attrs.length > 0) {
                                return (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {attrs.join(" | ")}
                                  </Typography>
                                );
                              }
                            } catch (e) {
                              console.error('Error processing attributes:', e);
                            }
                            return null;
                          })()}
                          {/* المستودع */}
                          {(() => {
                            const warehouseName = item.warehouseName;
                            if (warehouseName && typeof warehouseName === 'string' &&
                                !/[${}[\]<=>]/.test(warehouseName)) {
                              return (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  المستودع: {warehouseName}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </Box>
                        <Chip
                          label={`الكمية: ${item.quantity}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ flexShrink: 0, fontWeight: 'bold' }}
                        />
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      {/* تفاصيل الأسعار */}
                      <Stack spacing={0.5}>
                        {page?.enableTax && invoice.tax > 0 ? (
                          <>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">السعر (شامل الضريبة)</Typography>
                              <Typography variant="body2" fontWeight="medium">{formatCurrency(item.price)}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">السعر (بدون ضريبة)</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {(() => {
                                  const itemAny = item as any;
                                  const preTaxPrice = itemAny.preTaxPrice || (item.price / (1 + ((item.tax || 0) / 100)));
                                  return formatCurrency(preTaxPrice);
                                })()}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">نسبة الضريبة</Typography>
                              <Typography variant="body2" fontWeight="medium">{item.tax || 0}%</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">قيمة الضريبة</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {(() => {
                                  const itemAny = item as any;
                                  const taxAmount = itemAny.taxAmount || ((item.price - (item.price / (1 + ((item.tax || 0) / 100)))) * item.quantity);
                                  return formatCurrency(taxAmount);
                                })()}
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">السعر</Typography>
                            <Typography variant="body2" fontWeight="medium">{formatCurrency(item.price)}</Typography>
                          </Box>
                        )}
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            الخصم
                            {(() => {
                              const itemAny = item as any;
                              const discountPercent = itemAny.discountPercent;
                              if (discountPercent && discountPercent > 0) {
                                return ` (${discountPercent}%)`;
                              }
                              return '';
                            })()}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">{formatCurrency(item.discount || 0)}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" sx={{ pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight="bold">الإجمالي</Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">{formatCurrency(item.total)}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ ml: 'auto' }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ملخص الفاتورة
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={1}>
                {page?.enableTax && invoice.tax > 0 ? (
                  <>
                    {/* إذا كانت الضريبة مفعلة - استخدام القيم المحفوظة */}
                    <Grid item xs={6}>
                      <Typography variant="body1">المبلغ قبل الضريبة:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" align="left">
                        {formatCurrency(invoice.subtotal)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body1">قيمة الضريبة:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" align="left">
                        {formatCurrency(invoice.tax)}
                      </Typography>
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* إذا لم تكن الضريبة مفعلة */}
                    <Grid item xs={6}>
                      <Typography variant="body1">المجموع الفرعي:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" align="left">
                        {formatCurrency(invoice.subtotal)}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {/* عرض الخصم */}
                <Grid item xs={6}>
                  <Typography variant="body1">الخصم:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" align="left">
                    {formatCurrency(invoice.discount || 0)}
                  </Typography>
                </Grid>
                
                {/* المبلغ المدفوع والمتبقي إذا كانت متوفرة */}
                {(invoice as any).paid !== undefined && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body1">المبلغ المدفوع:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" align="left">
                        {formatCurrency((invoice as any).paid || 0)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body1">المبلغ المتبقي:</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" align="left" color={(invoice as any).remaining > 0 ? "error.main" : "success.main"}>
                        {formatCurrency((invoice as any).remaining || 0)}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6">الإجمالي:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" align="left" fontWeight="bold" color="primary.main">
                    {formatCurrency(invoice.total)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* قسم طرق الدفع */}
        {(invoice as any).paymentMethods && (invoice as any).paymentMethods.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  طرق الدفع
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1}>
                  {(invoice as any).paymentMethods.map((method: any, index: number) => (
                    <React.Fragment key={index}>
                      <Grid item xs={8}>
                        <Typography variant="body1">
                          {method.name || 'غير محدد'}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body1" align="left" fontWeight="medium">
                          {formatCurrency(method.amount || 0)}
                        </Typography>
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* قسم الكوبنات المطبقة */}
        {invoice.couponCodes && invoice.couponCodes.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  الكوبنات المطبقة
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1}>
                  {invoice.couponCodes.map((code, index) => (
                    <Grid item xs={12} key={index}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          الكوبن #{index + 1}:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                          {code}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                  {invoice.couponDiscount !== undefined && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }} />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          إجمالي خصم الكوبنات:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" align="left" fontWeight="bold" color="success.main">
                          {formatCurrency(invoice.couponDiscount)}
                        </Typography>
                      </Grid>
                      {invoice.couponDiscountType && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            نوع الخصم: {invoice.couponDiscountType === 'percentage' ? 'نسبة مئوية' : 'مبلغ ثابت'}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {invoice.notes && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ملاحظات
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1">{invoice.notes}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* قسم حالة الإرسال إلى هيئة الزكاة */}
        {invoice && (
          <Grid item xs={12}>
            <ZatcaSubmissionStatus
              invoice={invoice}
              page={page}
              onStatusChange={() => {
                // إعادة تحميل بيانات الفاتورة بعد تغيير الحالة
                fetchData();
              }}
            />
          </Grid>
        )}

        {/* قسم السدادات - يظهر فقط لفواتير مرتجع فاتورة استيراد */}
        {invoice && invoiceId && page && page.type === 'import_invoice_return' && (
          <Grid item xs={12}>
            <InvoicePaymentSection
              invoiceId={invoiceId}
              readOnly={invoice.status !== 'draft'}
              onPaymentComplete={() => {
                // إعادة تحميل بيانات الفاتورة بعد التسديد
                fetchData();
              }}
            />
          </Grid>
        )}
      </Grid>

      {/* Print Templates Dialog */}
      <Dialog open={printTemplatesDialog} onClose={() => setPrintTemplatesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>اختر قالب الطباعة</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            اختر قالب الطباعة المناسب للفاتورة:
          </Typography>
          <Grid container spacing={2}>
            {printTemplates.map((template) => (
              <Grid item xs={12} key={template.id}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handlePrintWithTemplate(template.id)}
                >
                  {template.name}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintTemplatesDialog(false)} color="primary">
            إلغاء
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceViewPage;